/**
 * Drops the word "Review"/"Reviews" from the star-rating count on product cards, so the collection
 * grid reads "(17)" rather than "(17 Reviews)".
 *
 * The text arrives server-side inside `product.metafields.okendo.StarRatingSnippet`, but stripping it
 * in Liquid is not enough on its own: Okendo re-hydrates `[data-oke-star-rating]` client side, and the
 * grid swaps its whole card list on filter and pagination. Both paths put the original markup back, so
 * the observer re-applies after each.
 */
const SELECTOR = '.eq-product-card__review .oke-sr-total-text';

/** @param {Element | null} element */
const stripReviewText = (element) => {
  if (!element) return;

  const current = element.textContent ?? '';
  // Takes the space with it, so " (17 Reviews)" becomes " (17)" rather than " (17 )".
  const next = current.replace(/\s*\bReviews?\b/gi, '');

  if (next !== current) element.textContent = next;
};

/** @param {Node} node */
const processNode = (node) => {
  // Replacing the count rewrites its text node rather than the span, so the added node is text and
  // the element to fix is its parent.
  if (!(node instanceof Element)) {
    stripReviewText(node.parentElement?.closest(SELECTOR) ?? null);
    return;
  }

  if (node.matches(SELECTOR)) {
    stripReviewText(node);
    return;
  }

  for (const element of node.querySelectorAll(SELECTOR)) stripReviewText(element);
};

const stripAll = () => {
  for (const element of document.querySelectorAll(SELECTOR)) stripReviewText(element);
};

stripAll();

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type === 'childList') {
      for (const node of mutation.addedNodes) processNode(node);
      continue;
    }

    // Okendo rewrites the count in place, which arrives as characterData rather than a new node.
    if (mutation.type === 'characterData') {
      stripReviewText(mutation.target.parentElement?.closest(SELECTOR) ?? null);
    }
  }
});

observer.observe(document.body, { childList: true, characterData: true, subtree: true });
